/**
 * 사용자 암호화 키 관리 서비스
 * 보안 강화를 위해 사용자별 고유 salt를 관리합니다.
 */

import { doc, getDoc, setDoc, updateDoc } from 'firebase/firestore';
import { db, auth } from '../config/firebase';
import { generateSecureRandom } from './webCryptoService';

// 메모리 캐시 - 세션 동안 사용자 salt를 캐싱
const saltCache = new Map();

/**
 * 안전한 랜덤 바이트 생성
 * @param {number} length - 바이트 길이
 * @returns {Uint8Array} 랜덤 바이트 배열
 */
function generateRandomBytes(length) {
  const array = new Uint8Array(length);
  crypto.getRandomValues(array);
  return array;
}

/**
 * ArrayBuffer를 Base64 문자열로 변환
 * @param {ArrayBuffer} buffer - 변환할 버퍼
 * @returns {string} Base64 문자열
 */
function arrayBufferToBase64(buffer) {
  const bytes = new Uint8Array(buffer);
  let binary = '';
  for (let i = 0; i < bytes.byteLength; i++) {
    binary += String.fromCharCode(bytes[i]);
  }
  return btoa(binary);
}

/**
 * 사용자의 암호화 salt 가져오기 또는 생성
 * @param {string} userUid - 사용자 UID
 * @returns {Promise<string>} Base64 인코딩된 salt
 */
export async function getUserEncryptionSalt(userUid) {
  if (!userUid) {
    throw new Error('사용자 UID가 필요합니다');
  }

  // 캐시 확인
  if (saltCache.has(userUid)) {
    return saltCache.get(userUid);
  }

  // 최대 3번 재시도 (Firestore rules 전파 대기)
  for (let attempt = 1; attempt <= 3; attempt++) {
    try {
      // 현재 인증 상태 확인
      if (!auth.currentUser) {
        if (process.env.NODE_ENV === 'development') {
          console.warn('사용자 인증 상태 확인 중...');
        }
        await new Promise(resolve => setTimeout(resolve, 100));
        continue;
      }
      
      const userKeyRef = doc(db, `users/${userUid}/security`, 'encryption');
      
      if (process.env.NODE_ENV === 'development') {
        console.log('시도 중:', {
          path: `users/${userUid}/security/encryption`,
          currentUser: auth.currentUser?.uid,
          userUid: userUid,
          match: auth.currentUser?.uid === userUid
        });
      }
      
      const userKeyDoc = await getDoc(userKeyRef);

      if (userKeyDoc.exists()) {
        const data = userKeyDoc.data();
        if (data.salt) {
          // 캐시에 저장
          saltCache.set(userUid, data.salt);
          return data.salt;
        }
      }

      // Salt가 없으면 새로 생성
      const newSalt = generateRandomBytes(32); // 256비트 salt
      const saltBase64 = arrayBufferToBase64(newSalt);

      await setDoc(userKeyRef, {
        salt: saltBase64,
        createdAt: new Date().toISOString(),
        version: '2.0' // 새로운 보안 버전
      });

      // 캐시에 저장
      saltCache.set(userUid, saltBase64);
      return saltBase64;
    } catch (error) {
      // 개발 모드에서만 로깅, 권한 오류는 일반적인 모바일 이슈이므로 덜 부담스럽게
      if (process.env.NODE_ENV === 'development') {
        if (error.message.includes('permissions')) {
          console.debug(`Enhanced security unavailable (attempt ${attempt}/3) - will use legacy encryption`);
        } else {
          console.error(`사용자 salt 가져오기 실패 (시도 ${attempt}/3):`, error.message);
        }
      }
      
      // 권한 에러이고 마지막 시도가 아니면 재시도
      if (error.message.includes('permissions') && attempt < 3) {
        // 500ms 대기 후 재시도
        await new Promise(resolve => setTimeout(resolve, 500));
        continue;
      }
      
      // 모든 시도 실패 시 fallback 에러
      throw new Error('FALLBACK_TO_LEGACY');
    }
  }
}

/**
 * 캐시 초기화 (로그아웃 시 호출)
 */
export function clearSaltCache() {
  saltCache.clear();
}

/**
 * 사용자의 고유 암호화 키 문자열 생성
 * @param {string} userUid - 사용자 UID
 * @param {string} userSalt - 사용자 고유 salt
 * @returns {string} 키 파생용 문자열
 */
export function createUserKeyString(userUid, userSalt) {
  // userUid와 salt를 조합하여 더 안전한 키 생성
  return `${userUid}:${userSalt}:newpass2-v2`;
}

/**
 * 레거시 데이터 마이그레이션 체크
 * @param {string} userUid - 사용자 UID
 * @returns {Promise<boolean>} 마이그레이션 필요 여부
 */
export async function needsMigration(userUid) {
  try {
    const userKeyRef = doc(db, `users/${userUid}/security`, 'encryption');
    const userKeyDoc = await getDoc(userKeyRef);

    if (!userKeyDoc.exists()) {
      return true; // 보안 문서가 없으면 마이그레이션 필요
    }

    const data = userKeyDoc.data();
    return data.version !== '2.0'; // 버전이 2.0이 아니면 마이그레이션 필요
  } catch (error) {
    return true; // 에러 발생 시 안전하게 마이그레이션 필요로 처리
  }
}

/**
 * 사용자 보안 설정 업데이트
 * @param {string} userUid - 사용자 UID
 * @param {Object} updates - 업데이트할 데이터
 */
export async function updateUserSecuritySettings(userUid, updates) {
  try {
    const userKeyRef = doc(db, `users/${userUid}/security`, 'encryption');
    await updateDoc(userKeyRef, {
      ...updates,
      updatedAt: new Date().toISOString()
    });
  } catch (error) {
    if (process.env.NODE_ENV === 'development') {
      console.error('보안 설정 업데이트 실패:', error.message);
    }
    throw new Error('보안 설정 업데이트 실패');
  }
}

export default {
  getUserEncryptionSalt,
  createUserKeyString,
  needsMigration,
  updateUserSecuritySettings
};